import { useState } from 'react'
import { useLongPress } from '../../../hooks/useLongPress'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import type { Message } from '../../../types/chat'
import { useNls } from '../../../nls/nlsProvider'
import { Avatar, Button, DeliveryTicks, Pressable, StatusChip } from '../../Common'
import { retryWhatsappMessage } from '../../../services/whatsappService'
import { AnalyticsCardBlock } from '../AnalyticsCardBlock'
import { ChartCardBlock } from '../ChartCardBlock'

type Props = {
  message: Message
  isSelf: boolean
  /** Position of this bubble within the author-run group. */
  position: 'first' | 'middle' | 'last' | 'only'
  /** Show avatar slot content (only on last-of-group for non-self). */
  showAvatar: boolean
  /** Author name shown only on first-of-group for non-self. */
  showAuthor: boolean
  /** Time shown only on last-of-group. */
  showTimestamp: boolean
  /** Parent message when this is a reply. */
  parent?: Message | null
  translation?: string
  /** Fires on long-press (touch) or right-click (desktop). */
  onOpenContext: (message: Message, isSelf: boolean) => void
  /** Fires when the user taps the reply-quote chip. */
  onJumpToParent?: (parentId: string) => void
  workspaceId?: string
  channelId?: string
  canRetryWhatsapp?: boolean
  onRetryWhatsapp?: () => Promise<void> | void
}

const formatTimestamp = (timestamp: unknown): string => {
  if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
    try {
      return (timestamp as { toDate: () => Date }).toDate().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    } catch {
      return ''
    }
  }
  return ''
}

const summarizeText = (text: string, cap = 90): string => {
  const trimmed = (text || '').replace(/\s+/g, ' ').trim()
  if (!trimmed) return ''
  return trimmed.length > cap ? `${trimmed.slice(0, cap - 1)}…` : trimmed
}

const LONG_PRESS_MS = 450

export const MemberChatBubble = ({
  message,
  isSelf,
  position,
  showAvatar,
  showAuthor,
  showTimestamp,
  parent,
  translation,
  onOpenContext,
  onJumpToParent,
  workspaceId,
  channelId,
  canRetryWhatsapp,
  onRetryWhatsapp,
}: Props) => {
  const { t } = useNls()
  const [retrying, setRetrying] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)

  const waMeta = message.metadata?.whatsapp
  const isOutbound = waMeta?.direction === 'OUT' || (Boolean(waMeta) && waMeta?.direction !== 'IN')
  const inRoles = canRetryWhatsapp !== undefined ? canRetryWhatsapp : true
  const showRetry = inRoles && (waMeta?.status === 'PAUSED' || waMeta?.status === 'FAILED')

  const handleRetryWhatsapp = async () => {
    if (retrying) return
    setRetrying(true)
    setRetryError(null)
    try {
      if (onRetryWhatsapp) {
        await onRetryWhatsapp()
      } else if (workspaceId && channelId) {
        await retryWhatsappMessage({
          workspaceId,
          channelId,
          messageId: message.id,
        })
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Refused to retry WhatsApp message'
      setRetryError(msg)
    } finally {
      setRetrying(false)
    }
  }

  const press = useLongPress({ ms: LONG_PRESS_MS, driftPx: 8, onLongPress: () => onOpenContext(message, isSelf) })
  const startPress = (event: React.PointerEvent) => { if (event.pointerType === 'touch') press.onPointerDown(event) }

  // TODO(orbit-phase-7-rollout): remove local onOpenContext long-press once Orbit is default-on.

  const bubbleClass = [
    'memberChatBubble',
    position === 'first' ? 'isFirst' : '',
    position === 'last' ? 'isLast' : '',
    position === 'only' ? 'isOnly' : '',
    message.isDeleted ? 'isDeleted' : '',
    message.optimisticStatus === 'failed' ? 'isFailed' : '',
  ].filter(Boolean).join(' ')

  const hasAuthorRow = (showAuthor && !isSelf) || waMeta?.via === 'PHONE' || waMeta?.imported || message.source === 'WHATSAPP' || waMeta?.direction === 'IN'

  return (
    <div className={`memberChatGroup ${isSelf ? 'isSelf' : ''}`}>
      <div className="memberChatGroup__avatarSlot" aria-hidden={!showAvatar}>
        {showAvatar && !isSelf ? (
          <Avatar
            photoURL={message.authorAvatarUrl}
            name={message.authorName}
            size="sm"
            className="memberChatGroup__avatar"
          />
        ) : null}
      </div>
      <div className="memberChatGroup__stack">
        {hasAuthorRow ? (
          <span className="memberChatGroup__author">
            {showAuthor && !isSelf ? message.authorName : null}
            {waMeta?.via === 'PHONE' && (
              <StatusChip tone="neutral" size="sm" className="chatMessageWhatsappTag" data-testid="whatsapp-phone-tag">
                {t('NLSChatTagViaPhone')}
              </StatusChip>
            )}
            {waMeta?.imported === true && (
              <StatusChip tone="neutral" size="sm" className="chatMessageWhatsappTag" data-testid="whatsapp-imported-tag">
                {t('NLSChatTagImported')}
              </StatusChip>
            )}
            {(message.source === 'WHATSAPP' || waMeta?.direction === 'IN') && (
              <StatusChip tone="success" size="sm" className="chatMessageInboundMarker" data-testid="whatsapp-inbound-marker">
                WhatsApp
              </StatusChip>
            )}
          </span>
        ) : null}
        <article
          className={bubbleClass}
          data-orbit-target="message-bubble"
          data-message-id={message.id}
          data-orbit-title={summarizeText(message.textContent, 40)}
          onPointerDown={startPress}
          onPointerUp={press.onPointerUp}
          onPointerCancel={press.onPointerCancel}
          onPointerMove={press.onPointerMove}
        >
          {parent ? (
            <Pressable
              className="memberChatReply"
              onClick={() => onJumpToParent?.(parent.id)}
              aria-label={t('NLSChatAriaReplyingTo', { author: parent.authorName })}
            >
              <span className="memberChatReply__author">{parent.authorName}</span>
              <span className="memberChatReply__preview">
                {parent.isDeleted ? t('NLSChatMessageDeleted') : summarizeText(parent.textContent) || t('NLSChatAttachmentGeneric')}
              </span>
            </Pressable>
          ) : null}
          {message.isDeleted ? (
            <span>{t('NLSChatMessageDeleted')}</span>
          ) : (
            <span>{message.textContent}</span>
          )}
          {!message.isDeleted ? <AnalyticsCardBlock card={message.metadata?.analyticsCard} /> : null}
          {!message.isDeleted && message.metadata?.chartCard ? <ChartCardBlock card={message.metadata.chartCard} /> : null}
          {translation ? <div className="memberChatBubble__meta" style={{ color: 'var(--muted)', marginLeft: 0 }}>{translation}</div> : null}
        </article>
        {showTimestamp || isOutbound ? (
          <div className="memberChatBubble__meta">
            {showTimestamp && <span>{formatTimestamp(message.timestamp)}</span>}
            {isOutbound && (
              <DeliveryTicks status={waMeta?.status} testId="whatsapp-delivery-ticks" />
            )}
            {isOutbound && waMeta?.status === 'FAILED' && (waMeta.failureTitle || waMeta.error) ? (
              <span className="memberChatBubble__failureText" data-testid="whatsapp-failure-text">
                {waMeta.failureTitle || waMeta.error}
              </span>
            ) : null}
            {showRetry ? (
              <Button
                size="sm"
                variant="tertiary"
                onClick={handleRetryWhatsapp}
                disabled={retrying}
                loading={retrying}
                data-testid="whatsapp-retry-button"
              >
                {t('NLSChatActionRetry')}
              </Button>
            ) : null}
            {retryError ? (
              <span className="memberChatBubble__retryError" data-testid="whatsapp-retry-error">
                {retryError}
              </span>
            ) : null}
            {isSelf && !message.isDeleted && !isOutbound && showTimestamp ? (
              <span className="memberChatBubble__seen" aria-hidden="true">
                <CheckRoundedIcon sx={{ fontSize: 12 }} />
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}
